import { useState } from 'react';
import { supabase } from '../../config/supabase';
import { Task } from '../../types/task';
import { useStore } from '../../store/useStore';
import { logger } from '../../utils/logger';
import toast from 'react-hot-toast';

export const useTaskActions = () => {
  const [isLoading, setIsLoading] = useState(false);
  const store = useStore();

  const updateTaskStatus = async (taskId: string, newStatus: Task['status']) => {
    try {
      setIsLoading(true);
      logger.info('Updating task status:', { taskId, newStatus });

      // Update task in Supabase
      const { data: task, error } = await supabase
        .from('tasks')
        .update({ 
          status: newStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', taskId)
        .select(`
          *,
          subtasks (*),
          comments:task_comments (
            *,
            user:profiles(name, avatar)
          )
        `)
        .single();

      if (error) throw error;

      // Update local store
      store.updateTaskStatus({ taskId, status: newStatus });
      
      // Create notification message
      if (store.currentUser) {
        const { error: messageError } = await supabase
          .from('messages')
          .insert({
            type: 'DIRECT',
            priority: task.priority,
            title: `Task Status Update: ${task.title}`,
            content: `${store.currentUser.name} has updated the task status to ${newStatus}`,
            sender_id: store.currentUser.id,
            departments: [task.department],
            is_direct: true,
            created_at: new Date().toISOString()
          });

        if (messageError) {
          logger.error('Failed to create status update message:', messageError);
        }
      }

      toast.success('Task status updated successfully');
      return true;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update task status';
      logger.error('Task status update error:', error);
      toast.error(message);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const acknowledgeTask = async (taskId: string) => {
    try {
      setIsLoading(true);
      logger.info('Acknowledging task:', taskId);

      // Update task in Supabase
      const { data: task, error } = await supabase
        .from('tasks')
        .update({ 
          acknowledged: true,
          acknowledged_at: new Date().toISOString(),
          status: 'IN_PROGRESS',
          updated_at: new Date().toISOString()
        })
        .eq('id', taskId)
        .select(`
          *,
          subtasks (*),
          comments:task_comments (
            *,
            user:profiles(name, avatar)
          )
        `)
        .single();

      if (error) throw error;

      // Update local store
      store.updateTaskAcknowledgment(taskId);
      store.updateTaskStatus({ taskId, status: 'IN_PROGRESS' });
      
      // Create notification message
      if (store.currentUser) {
        const { error: messageError } = await supabase
          .from('messages')
          .insert({
            type: 'DIRECT',
            priority: task.priority,
            title: `Task Acknowledged: ${task.title}`,
            content: `${store.currentUser.name} has acknowledged the task`,
            sender_id: store.currentUser.id,
            departments: [task.department],
            is_direct: true,
            created_at: new Date().toISOString()
          });

        if (messageError) {
          logger.error('Failed to create acknowledgment message:', messageError);
        }
      }

      toast.success('Task acknowledged successfully');
      return true;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to acknowledge task';
      logger.error('Task acknowledgment error:', error);
      toast.error(message);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    updateTaskStatus,
    acknowledgeTask,
    isLoading
  };
};